<template>
  <v-row class="no-padding-row">
    <v-col cols="1" class="align-vertical no-padding">
      {{ number }}
    </v-col>

    <v-col cols="2" class="align-vertical no-padding">
      <v-text-field
        v-model="itemName"
        @input="parseNewVal($event, 'name', `I/O / Pin ${number} / Name`)"
      />
    </v-col>

    <v-col cols="2" class="align-vertical no-padding">
      <v-select
        v-model="selectedTrigger"
        item-text="label"
        :items="triggerMap" 
        @change="parseNewVal($event, 'triggerLevel', `I/O / Pin ${number} / Trigger`)"
      />
    </v-col>

    <v-col cols="1" class="align-vertical no-padding">
      <v-checkbox
        v-model="enableAlert"
        @change="parseNewVal($event, 'alert', `I/O / Pin ${number} / Alert`)"
      />
    </v-col>

    <v-col cols="2" class="align-vertical no-padding">
      <v-select 
        v-model="selectedAction"
        item-text="label"
        return-object
        :items="actionMap"
        @change="parseNewVal($event, 'action', `I/O / Pin ${number} / Action`)"
      />
    </v-col>

    <v-col cols="1" class="align-vertical no-padding">
      <template>
        <v-icon 
          v-if="
            deviceModel == 'CAMERAMATICS_JH4-HD-4G' 
            || 
            deviceModel == 'CAMERAMATICS_JH8-HD-4G' 
            ||
            deviceModel == 'CAMERAMATICS_JH4_NT-HD-4G' 
            || 
            deviceModel == 'CAMERAMATICS_JH8_NT-HD-4G'
            ||
            deviceModel == 'CAMERAMATICS_JI7A-SD-4G'"
            :color="iconColour"
            @click="toggleAdditionalConfigOptions"
        >
          mdi-settings
        </v-icon>
      </template>

      
      <v-dialog
        v-model="displayLinkToCamera"
        width="700"
      >
        <LinkToCamera 
          :key="componentKey"
          :action="item"
          :pin-list="pinList"
          :channels="channels"
          :priority-config="priorityConfig"
          @closeDialog="displayLinkToCamera = false"
          @updatePriority="updatePriority"
          @setPriorities="parsePriorities"
          @refreshComp="refreshComp"
        />
      </v-dialog>

      <v-dialog
        v-model="displayDisableCamera"
        width="700"
      >
        <DisableCameras 

          :key="componentKey"
          :visible="displayDisableCamera"
          :action="item"
          :pin-list="pinList"
          :channels="channels"
          :disabled-cameras="disabledCameras"
          @closeDialog="displayDisableCamera = false"
          @updateDisabledCameras="updateDisabledCameras"
          @refreshComp="refreshComp"
        />
      </v-dialog>
    </v-col>
  </v-row>
</template>

<script>

import LinkToCamera from './LinkToCamera.vue';
import DisableCameras from './DisableCameras.vue';

import {deviceConfigMixin} from '@/mixins/deviceConfigMixin.js';
import { checkIfVersionIsAboveLimit } from '@/utils';

import { Logger } from "aws-amplify";
const logger = new Logger("IO Setting");

export default {
  name: 'IOSettings',
  components: {
    LinkToCamera,
    DisableCameras
  },  
  mixins: [deviceConfigMixin],
  props: {
    item: {
      type: Object,
      required: true
    },
    pinList: {
      type: Array,
      required: true
    },
    channels: {
      type: Array,
      required: true
    },
    deviceModel: {
      type: String,
      required: true
    },
    priorityConfig: {
      type: Array,
      required: true
    },
    disabledCameras: {
      type: Array,
      required: true
    },
    firmwareVersion: {
      type: String,
      required: true
    },
    
  },
  data() {
    return {
      triggerMap: [
        {
          xml: 'high',
          label: 'High'
        },
        {
          xml: 'low',
          label: 'Low'
        },
      ],
      selectedTrigger: null,
      selectedAction: null,
      enableAlert: Boolean,
      itemName: '',
      dialog: false,
      componentKey: 0,
      displayLinkToCamera: false,
      displayDisableCamera: false,
      iconColourOverWrite: false,
      supportMap: {
        'CAMERAMATICS_JH8_NT-HD-4G': {
          from: '01.02.09',
          to: '9.99.99'
        },
        'CAMERAMATICS_JH4_NT-HD-4G': {
          from: '01.02.09',
          to: '9.99.99'
        },
        'CAMERAMATICS_JH4-HD-4G': {
          from: '01.00.57',
          to: '9.99.99'
        },
        'CAMERAMATICS_JH8-HD-4G': {
          from: '01.00.57',
          to: '9.99.99'
        },
        'CAMERAMATICS_JI7A-SD-4G': {
          from: '0.00.00',
          to: '9.99.99'
        }
      },
      disableCamerasKey: 0
    }
  },
  computed: {
    iconColour() {
      if (!this.selectedAction) return ''
      if(this.selectedAction.xml == 'disableCameras') {
        return this.item.action[0].disableCameras || this.iconColourOverWrite ? '' : 'red'
      }
      else {
        return ''
      }
    },
    supportsDisableCameraKillswitch() {
      if (!this.supportMap.hasOwnProperty(this.deviceModel)) {
        return false
      }

      return checkIfVersionIsAboveLimit(this.firmwareVersion, this.supportMap[this.deviceModel]['from'])
    },
    actionMap() {
      if(this.supportsDisableCameraKillswitch) {
        return [
          {
            xml: 'none',
            label: 'None'
          },
          {
            xml: 'videoOutFullScreen',
            label: 'Link to Camera'
          },
          {
            xml: 'disableCameras',
            label: 'Disable Cameras'
          }
        ]
      } else {
        return [
          {
            xml: 'none',
            label: 'None'
          },
          {
            xml: 'videoOutFullScreen',
            label: 'Link to Camera'
          }
        ]
      }
    },
    number() {
      return this.item.number[0];
    },
    name() {
      return this.item.name[0];
    },
    alert() {
      return JSON.parse(this.item.alert[0].enable[0]);
    },
    triggerLevel() {
      return this.item.triggerLevel[0];
    },
    action() {
      return this.item.action[0].type[0];
    },
    mapList() {
      const list = [this.triggerMap, this.actionMap];

      return list.flat();
    }
  },
  watch: {
    iconColour(val) {
      if(val == 'red') {
        this.$emit('disableSendSave', this.item.number[0], true)
      } else {
        this.$emit('disableSendSave', this.item.number[0], false)
      }
    }
  },
  mounted() {
    this.initMapVals();
  },
  methods: {
    toggleAdditionalConfigOptions() {
      logger.info('toggleAdditionalConfigOptions', this.selectedAction)
      switch(this.selectedAction.xml) {
        case 'videoOutFullScreen':
          this.displayLinkToCamera = true
          break;
        case 'disableCameras':
          this.displayDisableCamera = true
          break;
        case 'none':
          break;
        default:
      }
    },
    refreshComp() {
      this.componentKey += 1;
    },
    initMapVals() {
      this.selectedTrigger = this.triggerMap.find(item => {
        return item.xml === this.triggerLevel
      });
      this.selectedAction = this.actionMap.find(action => {
        return action.xml === this.action;
      })
      this.itemName = this.name;
      this.enableAlert = this.alert;
    },
    parseNewVal(val, id, label, pin = false) {
      if(typeof val === 'object' && val !== null) {
        val = val.label
      }
      logger.info('test parse new val ', val, id, label)
      let payload = {
        newValue: this.returnMappedVal(val, [this.triggerMap, this.actionMap]),
        key: this.configPropMapper(id, pin),
        label: label,
        context: {
          newValue: pin ? pin : this.number,
          key: this.configPropMapper('number', pin),
          label: label
        }
      }

      this.$emit('settingsUpdated', payload, this.mapList);
      if (pin) return;
      // Add or remove config from config pool
      if (id == 'action') {
        let payload = {
          action: val,
          target: this.item
        }

        this.$emit('updateConfigPool', payload);
      }
    },
    configPropMapper(key, pin = false) {
      let number = pin ? pin : this.number;
      let keyBase = `device_config.peripheral[0].io[0].pin[${number - 1}]`;
      let map = {
        'number': `${keyBase}.number[0]`,
        'name': `${keyBase}.name[0]`,
        'alert': `${keyBase}.alert[0].enable[0]`,
        'triggerLevel': `${keyBase}.triggerLevel[0]`,
        'action': `${keyBase}.action[0].type[0]`
      }

      return map[key];
    },
    updateDisabledCameras(data) {
      logger.info('updateDisabledCameras', data, this.item)
      const pinNum = this.item.number[0]

      this.parseNewVal('Disable Cameras', 'action', `I/O / Pin ${parseInt(pinNum)} / Action`, parseInt(pinNum))
      
      for (const key in data) {
        if(data.hasOwnProperty(key) && data[key]) {
          let keyBase = `device_config.peripheral[0].io[0].pin[${parseInt(pinNum) - 1}]`
          let payload = {
            newValue: key,
            key: `${keyBase}.action[0].disableCameras[0].cameraChannel[${parseInt(key) - 1}]`,
            label: `I/O / Pin ${pinNum} / Disable Camera`
          }

          this.$emit('settingsUpdated', payload, this.mapList)
        }
        else if(data.hasOwnProperty(key) && !data[key]) {
          let keyBase = `device_config.peripheral[0].io[0].pin[${parseInt(pinNum) - 1}]`
          let payload = {
            newValue: 'removePropertyFromConfigObject',
            key: `${keyBase}.action[0].disableCameras[0].cameraChannel[${parseInt(key) - 1}]`,
            label: `I/O / Pin ${pinNum} / Disable Camera`
          }
          
          this.$emit('settingsUpdated', payload, this.mapList)
        }
        if(data[key]) {
          this.iconColourOverWrite = true
        }
        logger.info('this.iconColourOverWrite final', this.iconColourOverWrite)
      }
    },
    parsePriorities(data) {
      let newVals = [];
      data.forEach(config => {
        logger.info('set action type ', config)
        this.parseNewVal('Link to Camera', 'action', `I/O / Pin ${config.number[0]} / Action`, config.number[0])
      })
      data.forEach(config => {

        let keys = ['cameraChannel', 'priority'];
        let keyBase = `device_config.peripheral[0].io[0].pin[${config.number[0] - 1}]`;
        let map = {
          'cameraChannel': `${keyBase}.action[0].cameraChannel[0]`,
          'priority': `${keyBase}.action[0].priority[0]`
        };
        logger.info('config.number[0] ', config.number[0])
        keys.forEach(item => {
          let payload = {
            newValue: item == 'priority' ? config.action[0].priority[0] : config.action[0].cameraChannel[0],
            key: map[item],
            id: config.number[0],
            label: item == 'priority' ? `I/O / Pin ${config.number[0]} / Priority` : `I/O / Pin ${config.number[0]} / Camera Channel`,
            context: {
              newValue: config.number[0],
              key: `${keyBase}.number[0]`,
              label: item == 'priority' ? `I/O / Pin ${config.number[0]} / Priority` : `I/O / Pin ${config.number[0]} / Camera Channel`
            }
          }
          newVals.push(payload);
        });
      });
      
      logger.info('new vals', newVals)
      this.$emit('priorityUpdated', newVals);
    },
    updatePriority(data) {
      this.$emit('updatePriority', data);
    }
  }
}
</script>

<style lang="scss" scoped>
  .align-vertical {
    justify-content: center;
    align-items: center;
    display: flex;
  }
  .no-padding {
    padding: 0 0.25em !important;
    margin: 0 !important;
  }
  .no-padding-row {
    padding: 0 !important;
  }
  .acc-alerts {
    border: solid 2px;
    position: relative;
    .tile-title {
      position: absolute;
      top: -15px;
      left: 10px;
      background: white;
      font-size: 18px;
      font-weight: 400;
      padding: 0 .25em;
    }
  }
</style>